import { useMemo, useState } from "react";
import StatusBadge from "../../intelligence/StatusBadge";
import {
  VEHICLE_REVIEW_STATUSES,
  VEHICLE_SOURCE_NEEDS_REVIEW_FILTER,
  VEHICLE_SOURCE_SORT_OPTIONS,
  VEHICLE_SOURCE_STATUSES,
  VEHICLE_SOURCE_TYPES,
  formatVehicleSourceStatus,
  formatVehicleSourceType,
} from "../../../../constants/vehicleSourceConstants";
import AdminStatePanel from "../layout/AdminStatePanel";
import VehicleReviewStatusBadge from "./VehicleReviewStatusBadge";
import VehicleSourceFormModal from "./VehicleSourceFormModal";
import VehicleSourceReviewPanel from "./VehicleSourceReviewPanel";
import { useVehicleSourcesAdmin } from "./useVehicleSourcesAdmin";
import {
  REVIEW_QUICK_FILTERS,
  complianceProgress,
  sourceNeedsReview,
  sourceStatusTone,
} from "./vehicleSourceReviewUtils";

function formatDate(value) {
  if (!value) return "Not reviewed yet";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const ACTIVE_FILTER_OPTIONS = [
  { value: "", label: "All active states" },
  { value: "true", label: "Active only" },
  { value: "false", label: "Inactive only" },
];

const INGEST_FILTER_OPTIONS = [
  { value: "", label: "All ingest flags" },
  { value: "true", label: "Allowed to ingest" },
  { value: "false", label: "Not allowed" },
];

const VehicleSourceReviewPage = ({ onClose, onOpenDataSources }) => {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const admin = useVehicleSourcesAdmin({
    defaultSourceStatus: VEHICLE_SOURCE_NEEDS_REVIEW_FILTER,
  });

  const {
    canManage,
    sources,
    summary,
    loading,
    errorMessage,
    toast,
    search,
    setSearch,
    sourceType,
    setSourceType,
    sourceStatus,
    setSourceStatus,
    isActiveFilter,
    setIsActiveFilter,
    allowedFilter,
    setAllowedFilter,
    robotsReview,
    setRobotsReview,
    termsReview,
    setTermsReview,
    accessReview,
    setAccessReview,
    city,
    setCity,
    department,
    setDepartment,
    sort,
    setSort,
    sortDir,
    setSortDir,
    modalMode,
    selectedSource,
    loadSources,
    openModal,
    closeModal,
    handleSave,
    handleReviewSaved,
  } = admin;

  const kpi = summary || { total: sources.length };
  const queuePct = useMemo(() => {
    if (!kpi.total) return 0;
    return Math.min(100, Math.round(((kpi.needsReview ?? 0) / kpi.total) * 100));
  }, [kpi]);

  const activeQuickFilter = useMemo(() => {
    const match = REVIEW_QUICK_FILTERS.find((f) => f.value === sourceStatus);
    return match?.id || (sourceStatus ? "custom" : "all");
  }, [sourceStatus]);

  return (
    <div className="admin-page vehicle-source-review-page">
      <header className="admin-page__header vehicle-source-review-page__header">
        <div>
          <p className="admin-page__eyebrow">Vehicle Data · Compliance</p>
          <h1>Source Review</h1>
          <p className="admin-page__lede muted">
            Approve robots, terms, and access compliance before vehicle market ingestion runs.
            Review discovered sources and clear the queue without enabling live scraping here.
          </p>
        </div>
        <div className="admin-page__actions">
          {onOpenDataSources ? (
            <button type="button" className="admin-btn admin-btn--ghost" onClick={onOpenDataSources}>
              Data sources hub
            </button>
          ) : null}
          <button
            type="button"
            className="admin-btn admin-btn--secondary"
            onClick={loadSources}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          {onClose ? (
            <button type="button" className="admin-btn admin-btn--ghost" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {toast ? (
        <p className="admin-toast vehicle-source-review-page__toast" role="status">
          {toast}
        </p>
      ) : null}

      <section className="vehicle-source-review-page__summary">
        <article className="vehicle-source-review-page__stat vehicle-source-review-page__stat--accent">
          <span className="vehicle-source-review-page__stat-label">In review queue</span>
          <strong className="vehicle-source-review-page__stat-value">{kpi.needsReview ?? 0}</strong>
          <span className="muted">{queuePct}% of registry</span>
        </article>
        <article className="vehicle-source-review-page__stat">
          <span className="vehicle-source-review-page__stat-label">Showing now</span>
          <strong className="vehicle-source-review-page__stat-value">{sources.length}</strong>
          <span className="muted">Matching filters</span>
        </article>
        <article className="vehicle-source-review-page__stat">
          <span className="vehicle-source-review-page__stat-label">Approved</span>
          <strong className="vehicle-source-review-page__stat-value">{kpi.approved ?? 0}</strong>
          <span className="muted">Ready for ingest gate</span>
        </article>
        <article className="vehicle-source-review-page__stat">
          <span className="vehicle-source-review-page__stat-label">Allowed to ingest</span>
          <strong className="vehicle-source-review-page__stat-value">{kpi.allowedToIngest ?? 0}</strong>
          <span className="muted">Compliance + flags</span>
        </article>
        <article className="vehicle-source-review-page__stat">
          <span className="vehicle-source-review-page__stat-label">Total sources</span>
          <strong className="vehicle-source-review-page__stat-value">{kpi.total ?? 0}</strong>
          <span className="muted">{kpi.blocked ?? 0} blocked</span>
        </article>
      </section>

      <section className="vehicle-source-review-page__toolbar panel-card">
        <div className="vehicle-source-review-page__search-row">
          <label className="vehicle-source-review-page__search">
            <span className="vehicle-source-review-page__search-label">Search</span>
            <input
              type="search"
              placeholder="Search by name, key, or URL…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search vehicle sources"
            />
          </label>
          <button
            type="button"
            className={`admin-btn admin-btn--ghost vehicle-source-review-page__filter-toggle${showAdvancedFilters ? " is-active" : ""}`}
            onClick={() => setShowAdvancedFilters((open) => !open)}
            aria-expanded={showAdvancedFilters}
          >
            {showAdvancedFilters ? "Hide filters" : "More filters"}
          </button>
        </div>

        <div className="vehicle-source-review-page__chips" role="tablist" aria-label="Quick filters">
          {REVIEW_QUICK_FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              role="tab"
              aria-selected={activeQuickFilter === filter.id}
              className={`admin-btn admin-btn--nav${activeQuickFilter === filter.id ? " is-active" : ""}`}
              onClick={() => setSourceStatus(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {showAdvancedFilters ? (
          <div className="vehicle-source-review-page__advanced-filters">
            <select value={sourceType} onChange={(e) => setSourceType(e.target.value)} aria-label="Source type">
              <option value="">All types</option>
              {VEHICLE_SOURCE_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select value={sourceStatus} onChange={(e) => setSourceStatus(e.target.value)} aria-label="Source status">
              <option value="">All statuses</option>
              <option value={VEHICLE_SOURCE_NEEDS_REVIEW_FILTER}>Needs review (queue)</option>
              {VEHICLE_SOURCE_STATUSES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select value={robotsReview} onChange={(e) => setRobotsReview(e.target.value)} aria-label="Robots review">
              <option value="">Robots: all</option>
              {VEHICLE_REVIEW_STATUSES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select value={termsReview} onChange={(e) => setTermsReview(e.target.value)} aria-label="Terms review">
              <option value="">Terms: all</option>
              {VEHICLE_REVIEW_STATUSES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select value={accessReview} onChange={(e) => setAccessReview(e.target.value)} aria-label="Access review">
              <option value="">Access: all</option>
              {VEHICLE_REVIEW_STATUSES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select value={allowedFilter} onChange={(e) => setAllowedFilter(e.target.value)} aria-label="Ingest allowed">
              {INGEST_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select value={isActiveFilter} onChange={(e) => setIsActiveFilter(e.target.value)} aria-label="Active filter">
              {ACTIVE_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <input type="text" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
            <input
              type="text"
              placeholder="Department"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
            />
            <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort field">
              {VEHICLE_SOURCE_SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  Sort: {opt.label}
                </option>
              ))}
            </select>
            <select value={sortDir} onChange={(e) => setSortDir(e.target.value)} aria-label="Sort direction">
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        ) : null}
      </section>

      {loading ? <AdminStatePanel variant="loading" message="Loading review queue…" /> : null}
      {!loading && errorMessage ? (
        <AdminStatePanel variant="error" message={errorMessage} onRetry={loadSources} />
      ) : null}

      {!loading && !errorMessage ? (
        <section className="vehicle-source-review-page__list">
          {sources.length === 0 ? (
            <div className="vehicle-source-review-page__empty panel-card">
              <h2>No sources match these filters</h2>
              <p className="muted">
                Try the <strong>Review queue</strong> or <strong>All sources</strong> quick filter, or clear search text.
              </p>
              <button
                type="button"
                className="admin-btn admin-btn--secondary"
                onClick={() => {
                  setSearch("");
                  setSourceStatus(VEHICLE_SOURCE_NEEDS_REVIEW_FILTER);
                }}
              >
                Reset to review queue
              </button>
            </div>
          ) : (
            sources.map((source) => {
              const progress = complianceProgress(source);
              const needsReview = sourceNeedsReview(source);
              return (
                <article
                  key={source.id}
                  className={`vehicle-source-review-card panel-card${needsReview ? " vehicle-source-review-card--pending" : ""}`}
                >
                  <div className="vehicle-source-review-card__main">
                    <div className="vehicle-source-review-card__identity">
                      <div className="vehicle-source-review-card__title-row">
                        <h2 className="vehicle-source-review-card__title">{source.sourceName}</h2>
                        <StatusBadge
                          label={formatVehicleSourceType(source.sourceType)}
                          tone="neutral"
                        />
                        <StatusBadge
                          label={formatVehicleSourceStatus(source.sourceStatus)}
                          tone={sourceStatusTone(source.sourceStatus)}
                        />
                      </div>
                      <p className="vehicle-source-review-card__key muted">{source.sourceKey}</p>
                      {source.baseUrl ? (
                        <a
                          className="vehicle-source-review-card__url"
                          href={source.baseUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {source.baseUrl}
                        </a>
                      ) : null}
                    </div>

                    <div className="vehicle-source-review-card__compliance">
                      <div className="vehicle-source-review-card__compliance-header">
                        <span>Compliance checks</span>
                        <strong>
                          {progress.approved}/{progress.total} approved
                        </strong>
                      </div>
                      <div className="vehicle-source-review-card__compliance-bars">
                        {progress.checks.map((check) => (
                          <div key={check.key} className="vehicle-source-review-card__compliance-item">
                            <span className="vehicle-source-review-card__compliance-label">{check.label}</span>
                            <VehicleReviewStatusBadge status={check.status} />
                          </div>
                        ))}
                      </div>
                      <div
                        className="vehicle-source-review-card__progress"
                        role="progressbar"
                        aria-valuenow={progress.approved}
                        aria-valuemin={0}
                        aria-valuemax={progress.total}
                        aria-label="Compliance progress"
                      >
                        <span
                          className="vehicle-source-review-card__progress-fill"
                          style={{ width: `${(progress.approved / progress.total) * 100}%` }}
                        />
                      </div>
                    </div>

                    <div className="vehicle-source-review-card__meta">
                      <dl>
                        <div>
                          <dt>Ingest</dt>
                          <dd>
                            <StatusBadge
                              label={source.allowedToIngest ? "Allowed" : "Blocked"}
                              tone={source.allowedToIngest ? "success" : "neutral"}
                            />
                          </dd>
                        </div>
                        <div>
                          <dt>Active</dt>
                          <dd>{source.isActive ? "Yes" : "No"}</dd>
                        </div>
                        <div>
                          <dt>Coverage</dt>
                          <dd>{source.coverageArea || source.city || "—"}</dd>
                        </div>
                        <div>
                          <dt>Priority</dt>
                          <dd>{source.priority}</dd>
                        </div>
                        <div>
                          <dt>Last reviewed</dt>
                          <dd>{formatDate(source.reviewedAt)}</dd>
                        </div>
                      </dl>
                    </div>
                  </div>

                  <div className="vehicle-source-review-card__actions">
                    {canManage ? (
                      <button
                        type="button"
                        className="admin-btn admin-btn--primary"
                        onClick={() => openModal("review", source)}
                      >
                        Review source
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="admin-btn admin-btn--secondary"
                      onClick={() => openModal("view", source)}
                    >
                      View details
                    </button>
                    {needsReview ? (
                      <span className="vehicle-source-review-card__flag">Needs review</span>
                    ) : (
                      <span className="vehicle-source-review-card__flag vehicle-source-review-card__flag--clear">
                        Cleared
                      </span>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </section>
      ) : null}

      {modalMode === "review" ? (
        <VehicleSourceReviewPanel
          source={selectedSource}
          canManage={canManage}
          onClose={closeModal}
          onSaved={handleReviewSaved}
        />
      ) : null}

      {modalMode && modalMode !== "review" ? (
        <VehicleSourceFormModal
          mode={modalMode}
          source={selectedSource}
          onClose={closeModal}
          onSave={handleSave}
          canManage={canManage}
        />
      ) : null}
    </div>
  );
};

export default VehicleSourceReviewPage;
